import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { normalizeIranPhone, isValidIranPhone } from '@/lib/phone';
import TurnstileWidget from './TurnstileWidget';
import '../auth.css';

const GENERIC_ERROR = 'خطایی رخ داد. لطفاً دوباره تلاش کنید.';

// One translator for every step: the backend can return any of these
// messages from request-otp, verify-otp or reset-password.
const translateError = (error) => {
  const message = error?.message || '';

  if (message.includes('Too many requests') || message.includes('otp_blocked')) {
    return 'تعداد درخواست‌های شما بیش از حد مجاز است. لطفاً ۳۰ دقیقه دیگر دوباره تلاش کنید.';
  }
  if (message.includes('otp_daily_cap')) {
    return 'سقف پیامک روزانه این شماره تکمیل شده است. لطفاً فردا دوباره تلاش کنید.';
  }
  if (message.includes('otp_cooldown')) {
    return 'لطفاً چند دقیقه صبر کنید و سپس دوباره درخواست دهید.';
  }
  if (message.includes('otp_verify_daily_cap')) {
    return 'سقف تلاش‌های روزانه برای تایید کد تکمیل شده است. لطفاً فردا دوباره تلاش کنید.';
  }
  if (message.includes('captcha_required')) {
    return 'لطفاً تایید امنیت را تکمیل کنید.';
  }
  if (message.includes('captcha_failed')) {
    return 'تایید امنیت ناموفق بود. لطفاً دوباره تلاش کنید.';
  }
  if (message.includes('captcha_unavailable')) {
    return 'سرویس تایید امنیت در دسترس نیست. لطفاً چند لحظه بعد دوباره تلاش کنید.';
  }
  if (message.includes('Invalid or expired code')) {
    return 'کد وارد شده اشتباه یا منقضی شده است.';
  }
  return GENERIC_ERROR;
};

export default function ForgotPassword() {
  // step: 'lookup' -> phone + national ID, 'otp' -> verify code,
  //       'reset' -> new password, 'done' -> success
  const [step, setStep] = useState('lookup');

  const [mobilePhone, setMobilePhone] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [verifiedToken, setVerifiedToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Turnstile state
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  const navigate = useNavigate();

  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

  const handlePhoneChange = (event) => {
    setMobilePhone(normalizeIranPhone(event.target.value));
  };

  // ---- Step 1: phone + national ID, send OTP -------------------------

  const handleLookup = async (event) => {
    event.preventDefault();
    setError('');

    if (!isValidIranPhone(mobilePhone)) {
      setError('شماره موبایل باید ۱۰ رقم و بدون صفر باشد (مثلا ۹۱۲۳۴۵۶۷۸۹)');
      return;
    }
    if (nationalId.length !== 10) {
      setError('کد ملی باید دقیقاً ۱۰ رقم باشد');
      return;
    }

    // Require Turnstile token if site key is configured
    if (turnstileSiteKey && !turnstileToken) {
      setError('لطفاً تایید امنیت را تکمیل کنید.');
      return;
    }

    setLoading(true);
    try {
      const headers = {};
      if (turnstileToken) {
        headers['X-Turnstile-Token'] = turnstileToken;
      }

      const { error: apiError } = await apiClient.POST(
        '/api/v1/auth/forgot-password/request-otp',
        { body: { mobile_phone: mobilePhone, national_id: nationalId }, headers }
      );

      if (apiError) throw new Error(apiError.detail || GENERIC_ERROR);

      // Reset Turnstile after successful request
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken('');

      // Always proceed to OTP step (uniform response)
      setStep('otp');
    } catch (err) {
      setError(translateError(err));
      // Reset Turnstile on error so user can retry
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken('');

      // If backend requires captcha but no site key is configured, show config error
      if (err.message.includes('captcha_required') && !turnstileSiteKey) {
        setError('پیکربندی تایید امنیت ناقص است. لطفاً با پشتیبانی تماس بگیرید.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ---- Step 2: verify OTP ---------------------------------------------

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    setError('');

    if (otpCode.length !== 5) {
      setError('کد تایید باید ۵ رقم باشد');
      return;
    }

    setLoading(true);
    try {
      const { data, error: apiError } = await apiClient.POST(
        '/api/v1/auth/forgot-password/verify-otp',
        { body: { mobile_phone: mobilePhone, code: otpCode } }
      );

      if (apiError || !data?.verified_token) {
        throw new Error(apiError?.detail || 'Invalid or expired code');
      }

      setVerifiedToken(data.verified_token);
      setStep('reset');
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  };

  // ---- Step 3: set new password ----------------------------------------

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('رمز عبور باید حداقل ۸ کاراکتر باشد');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('رمز عبور و تکرار آن مطابقت ندارند');
      return;
    }

    setLoading(true);
    try {
      const { error: apiError } = await apiClient.POST('/api/v1/auth/reset-password', {
        body: { verified_token: verifiedToken, new_password: newPassword },
      });

      if (apiError) throw new Error(apiError.detail || GENERIC_ERROR);

      // Don't keep secrets in state once they are no longer needed.
      setVerifiedToken('');
      setNewPassword('');
      setConfirmPassword('');
      setStep('done');
    } catch (err) {
      setError('خطا در تغییر رمز عبور. لطفاً دوباره تلاش کنید.');
      console.error('Reset password error:', err);
    } finally {
      setLoading(false);
    }
  };

  const goBackToLookup = () => {
    setStep('lookup');
    setOtpCode('');
    setError('');
  };

  const goToLogin = () => {
    navigate('/login', {
      state: { message: 'رمز عبور شما تغییر کرد. اکنون وارد شوید.' },
    });
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <h2>بازیابی رمز عبور</h2>

        {error && <div className="error-message">{error}</div>}

        {step === 'lookup' && (
          <form onSubmit={handleLookup}>
            <div className="form-group">
              <label htmlFor="mobile_phone">شماره موبایل</label>
              <input
                id="mobile_phone"
                type="tel"
                inputMode="numeric"
                placeholder="شماره تماس بدون صفر (مثلا ۹۱۲۳۴۵۶۷۸۹)"
                value={mobilePhone}
                onChange={handlePhoneChange}
                maxLength={10}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="national_id">کد ملی</label>
              <input
                id="national_id"
                type="text"
                inputMode="numeric"
                placeholder="کد ملی ۱۰ رقمی"
                value={nationalId}
                onChange={(e) =>
                  setNationalId(e.target.value.replace(/\D/g, '').slice(0, 10))
                }
                maxLength={10}
                required
              />
            </div>

            {turnstileSiteKey && (
              <div className="form-group">
                <TurnstileWidget
                  ref={turnstileRef}
                  siteKey={turnstileSiteKey}
                  action="reset_otp"
                  onToken={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken('')}
                  onError={() => setTurnstileToken('')}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (turnstileSiteKey && !turnstileToken)}
              className="btn btn-primary"
            >
              {loading ? 'در حال بررسی...' : 'ادامه'}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={handleVerifyOtp}>
            <p>کد تایید به شماره {mobilePhone} ارسال شد.</p>

            <div className="form-group">
              <label htmlFor="otpCode">کد تایید</label>
              <input
                id="otpCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={5}
                value={otpCode}
                onChange={(e) =>
                  setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 5))
                }
                required
              />
            </div>

            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'در حال بررسی...' : 'تایید کد'}
            </button>
            <button
              type="button"
              className="btn-link"
              onClick={goBackToLookup}
              disabled={loading}
            >
              بازگشت
            </button>
          </form>
        )}

        {step === 'reset' && (
          <form onSubmit={handleResetPassword}>
            <div className="form-group">
              <label htmlFor="newPassword">رمز عبور جدید</label>
              <div className="password-field">
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="حداقل ۸ کاراکتر"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((show) => !show)}
                  aria-label={showPassword ? 'مخفی کردن رمز عبور' : 'نمایش رمز عبور'}
                  tabIndex={-1}
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmNewPassword">تکرار رمز عبور جدید</label>
              <input
                id="confirmNewPassword"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="تکرار رمز عبور"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8}
                required
              />
            </div>

            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'در حال ثبت...' : 'تغییر رمز عبور'}
            </button>
          </form>
        )}

        {step === 'done' && (
          <div>
            <div
              className="success-message"
              style={{
                background: '#d1fae5',
                color: '#065f46',
                padding: '0.75rem',
                borderRadius: '8px',
                marginBottom: '1rem',
              }}
            >
              رمز عبور شما با موفقیت تغییر یافت.
            </div>
            <button type="button" className="btn btn-primary" onClick={goToLogin}>
              ورود به حساب کاربری
            </button>
          </div>
        )}

        {step !== 'done' && (
          <p className="signup-link">
            <Link to="/login">بازگشت به صفحه ورود</Link>
          </p>
        )}
      </div>
    </div>
  );
}
